import Auth from '../api/auth';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
} from '../store/utils/api';

// A 401 from this API means one of two very different things:
//
//   authentication failed — the session/token is dead, the user must be signed out.
//     devise_token_auth renders `{ errors: ['You need to sign in or sign up ...'] }`
//
//   authorization failed — the user is signed in perfectly well, Pundit just refused
//     this particular action. EVERY application-level 401 in this codebase takes this
//     shape: `{ error: '<message>' }` with a STRING, via render_unauthorized in
//     app/controllers/concerns/request_exception_handler.rb (plus platform_controller,
//     team_members_controller, widgets_controller and the marketing_* controllers).
//
// Only the first should ever log anyone out. Treating every 401 as a dead session
// signed non-admin agents out of the dashboard the moment a screen fetched an
// admin-only endpoint — GET /api/v1/accounts/1/integrations/apps, which the inbox view
// requests on load, returns Pundit 401 for the agent role. Admins were unaffected
// because they are authorized for it. Live incident, prod, 2026-07-31.
//
// Deliberately fails CLOSED (no logout) on anything ambiguous: leaving a stale error
// on screen that a refresh clears is far cheaper than kicking a working agent out of
// the conversation they are in the middle of answering.
const isDeadSessionResponse = error => {
  const data = error?.response?.data;
  if (!data) return false;
  // an explicit authorization denial is not a session problem
  if (typeof data.error === 'string') return false;

  return Array.isArray(data.errors) && data.errors.length > 0;
};

const parseErrorCode = error => {
  if (
    error?.response?.status === 401 &&
    Auth.hasAuthCookie() &&
    isDeadSessionResponse(error)
  ) {
    // same cleanup order as api/auth.js#logout — drop the cached store before the cookies
    deleteIndexedDBOnLogout();
    clearCookiesOnLogout();
  }

  return Promise.reject(error);
};

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });
  // Add Auth Headers to requests if logged in
  if (Auth.hasAuthCookie()) {
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();
    Object.assign(wootApi.defaults.headers.common, {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    });
  }
  // Response parsing interceptor
  wootApi.interceptors.response.use(
    response => response,
    error => parseErrorCode(error)
  );
  return wootApi;
};
